import { CHROME } from '../../Theme/palette';
import { SoftTag } from '../../Tag/SoftTag';
import { ServiceTypeTag } from '../../Tag/ServiceTypeTag';
import { AddToPurchaseOrderModal, SharedCard } from '@components';
import { useAddToPurchaseOrder, useAccess, useTenantVat, useView } from '@hooks';
import { DispachedServicePart, InventoryLookup } from '@services';
import { useStore } from '@stores';
import { Utils } from '@utils';
import { Col, Row, Typography, message } from 'antd';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { inventoryToServicePart } from '../../ServiceOrder/OrderServiceSelect/inventoryToServicePart';
import { IServiceDetailsCardProps } from './ServiceDetailsCard.props';
import { ServiceDetailsPresentation } from './ServiceDetailsCard.presentation';
import { ServiceDetailsMobile } from './ServiceDetailsCard.mobile';
import { ServiceDetailsTechnician } from './ServiceDetailsCard.technician';

const { Text } = Typography;

export const ServiceDetailsCard: React.FC<IServiceDetailsCardProps> = observer(
  ({ service, disabled = false, variant = 'selected', basket, onRemove, canRemove, onUploadParts, onReturnParts }) => {
    const { isMobile } = useView();
    const vat = useTenantVat();
    const { serviceStore, orderStore } = useStore();
    const { addFromPart } = useAddToPurchaseOrder();

    const [pendingPOPart, setPendingPOPart] = useState<{
      part: DispachedServicePart;
      registrationNumber?: string;
      vin?: string;
      orderId?: string;
      masterBuildId?: string;
      buildReference?: string;
    } | null>(null);
    const [poModalLoading, setPoModalLoading] = useState(false);
    const { order: access } = useAccess();
    const readOnly = !access.editServiceDetails;

    const isAdditional = variant === 'additional';
    const isRevision = variant === 'revision';
    // A basket has no additional/revision sets - there is only ever the one job in it.
    const partsMap = basket
      ? basket.selectedServiceParts
      : isAdditional
        ? serviceStore.additionalServiceParts
        : isRevision
          ? serviceStore.revisionServiceParts
          : serviceStore.selectedServiceParts;
    const parts = partsMap.get(service.id) || [];

    const subTotal = useMemo(() => {
      const priceAmount = service.isFixed ? service.price : service.price * service.duration;
      const discountAmount = priceAmount * (service.discount / 100);
      return Utils.roundToTwoDecimals(priceAmount - discountAmount);
    }, [service]);

    const vatAmount = useMemo(() => {
      return service.isVat ? Utils.roundToTwoDecimals((subTotal * vat) / 100) : 0;
    }, [subTotal, vat, service]);

    // Total includes parts
    const partsTotal = useMemo(() => {
      const partsPrice = Utils.calculatePartsPrice(parts);
      const partsVat = parts.reduce(
        (acc, p) => acc + Utils.calculateVat({ ...p, duration: p.quantity, vat }),
        0
      );
      return partsPrice + partsVat;
    }, [parts, vat]);

    const total = Utils.roundToTwoDecimals(subTotal + vatAmount + partsTotal);

    const dispatchParts = basket
      ? basket.dispatchSelectedServiceParts
      : isAdditional
        ? serviceStore.dispatchAdditionalServiceParts
        : isRevision
          ? serviceStore.dispatchRevisionServiceParts
          : serviceStore.dispatchSelectedServiceParts;

    const updateField = basket
      ? basket.updateServiceField
      : isAdditional
        ? serviceStore.updateAdditionalServiceField
        : isRevision
          ? serviceStore.updateRevisionServiceField
          : serviceStore.updateServiceField;

    const sharedProps = {
      service,
      disabled,
      onRemove,
      canRemove,
      vat,
      showDiscount: access.discount,
      showTotals: access.totals,
      subTotal,
      vatAmount,
      total,
      parts,
      onAddPart: () => dispatchParts({ type: 'ADD_SERVICE_PART', serviceId: service.id }),
      onUploadParts,
      onReturnParts,
      onAddPartFromInventory: (item: InventoryLookup) =>
        dispatchParts({
          type: 'ADD_SERVICE_PART_FROM_INVENTORY',
          serviceId: service.id,
          servicePart: inventoryToServicePart(item),
        }),
      onRemovePart: (index: number) => dispatchParts({ type: 'REMOVE_SERVICE_PART', serviceId: service.id, index }),
      onUpdatePart: (index: number, values: any) => dispatchParts({ type: 'CHANGE_SERVICE_PART', serviceId: service.id, index, servicePart: values }),
      onAddPartToPurchaseOrder: (index: number) => {
        const part = parts[index];
        if (!part) return;
        if (!part.name?.trim()) {
          message.error('Enter a part name first');
          return;
        }
        const order = orderStore.order;
        setPendingPOPart({
          part,
          registrationNumber: order?.registrationNumber ?? undefined,
          vin: order?.vin ?? undefined,
          orderId: order?.id ?? undefined,
          masterBuildId: order?.masterBuildId ?? undefined,
          buildReference: order?.masterBuildNumber ?? undefined,
        });
      },
      handlePriceUpdate: (id: number, price: number) => updateField(id, 'price', price),
      handleDurationUpdate: (id: number, duration: number) => {
        updateField(id, 'duration', duration);
        // For the order being edited, keep its end time anchored to the total service duration.
        // A basket has no saved order behind it, so there is nothing to anchor.
        if (!basket && !isAdditional && !isRevision) {
          const totalHours = Utils.sumHours(serviceStore.selectedServices.map((s) => s.duration));
          orderStore.syncEndFromServiceDuration(totalHours);
        }
      },
      handleDiscountUpdate: (id: number, discount: number) => updateField(id, 'discount', discount),
      handleNoteUpdate: (id: number, note: string) => updateField(id, 'note', note),
    };

    const accentCardStyle: React.CSSProperties = {
      borderColor: CHROME.border,
    };

    const renderContent = () => {
      if (readOnly) {
        return <ServiceDetailsTechnician {...sharedProps} />;
      }
      if (isMobile) {
        return <ServiceDetailsMobile {...sharedProps} />;
      }
      return <ServiceDetailsPresentation {...sharedProps} />;
    };

    const poModal = (
      <AddToPurchaseOrderModal
        open={!!pendingPOPart}
        part={{
          name: pendingPOPart?.part.name ?? '',
          number: pendingPOPart?.part.number,
          reference: pendingPOPart?.part.reference,
          type: pendingPOPart?.part.type,
        }}
        context={{
          registrationNumber: pendingPOPart?.registrationNumber,
          vin: pendingPOPart?.vin,
        }}
        initialQty={pendingPOPart?.part.quantity ?? 0}
        loading={poModalLoading}
        onConfirm={async (qty) => {
          if (!pendingPOPart) return;
          setPoModalLoading(true);
          try {
            await addFromPart(
              pendingPOPart.part,
              {
                registrationNumber: pendingPOPart.registrationNumber,
                vin: pendingPOPart.vin,
                orderId: pendingPOPart.orderId,
                masterBuildId: pendingPOPart.masterBuildId,
                buildReference: pendingPOPart.buildReference,
              },
              qty,
            );
          } finally {
            setPoModalLoading(false);
            setPendingPOPart(null);
          }
        }}
        onCancel={() => setPendingPOPart(null)}
      />
    );

    if (!isMobile && !readOnly) {
      return <>{poModal}<div style={{ padding: '8px 0' }}>{renderContent()}</div></>;
    }

    return (
      <>
      {poModal}
      <Row gutter={isMobile ? [4, 8] : [16, 16]} style={{ marginTop: isMobile ? 16 : 12, marginBottom: isMobile ? 16 : 0 }}>
        <Col span={24}>
          <SharedCard
            collapsible={isMobile && !readOnly}
            defaultExpanded
            style={accentCardStyle}
            title={isMobile && !readOnly ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Text ellipsis={{ tooltip: service.name }} style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0 }}>{service.name}</Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <ServiceTypeTag isFixed={service.isFixed} style={{ fontSize: 14, padding: '2px 8px' }} />
                  {access.totals && (
                    <SoftTag group="blue" style={{ fontSize: 14, fontWeight: 700, padding: '2px 8px', margin: 0 }}>
                      {Utils.formatCurrency(total)}
                    </SoftTag>
                  )}
                </div>
              </div>
            ) : undefined}
          >
            {renderContent()}
          </SharedCard>
        </Col>
      </Row>
      </>
    );
  }
);
